import {InstantUtils} from '@common-module/utils/instant.utils';
import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {PageEvent} from '@angular/material/paginator';
import {Sort} from '@angular/material/sort';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {InstantRange} from '@common-module/app-interfaces/instant-range';
import {AuthUserService} from '@common-module/app-services/auth-user.service';
import {UiSpinnerService} from '@common-module/app-services/ui-spinner.service';
import {ViewBreakpointService, ViewSize} from '@common-module/app-services/view-breakpoint.service';
import {DriverHistory} from '@common-module/modelinterfaces/driver-history.model';
import {Driver} from '@common-module/modelinterfaces/driver.model';
import {FilterOperation} from '@common-module/modelinterfaces/enums/filter-operation';
import {PageParams} from '@common-module/modelinterfaces/page-params.model';
import {UnitShort} from '@common-module/modelinterfaces/unit-short.model';
import {DriverHistoryService} from '@common-module/services/driver-history.service';
import {ServerTablePagination} from '@common-module/table-config/server-table-pagination';
import {DriverState} from '../../../../shared/constants/enums/driver-state.enum';
import {DriverManagementService} from '../../../../shared/services/driver-management.service';
import {UnitGroupsService} from '../../../../shared/services/unit-groups.service';
import {
  CheckboxListFilterComponent
} from '@common-module/app-components/filters/checkbox-list-filter/checkbox-list-filter.component';
import {
  DateRangeFilterComponent
} from '@common-module/app-components/filters/date-range-filter/date-range-filter.component';
import {ButtonBackComponent} from '@common-module/buttons/base/concrete/button-back/button-back.component';
import {TranslateModule} from '@ngx-translate/core';
import {CommonModule} from "@angular/common";
import {MaterialModule} from "@common-module/material.module";
import {DriverHistoryTableComponent} from './driver-history-table/driver-history-table.component';

interface FiltersSettings {
  [p: string]: {
    operator: FilterOperation;
    value: string | InstantRange;
  };
}

@Component({
  selector: 'app-driver-history',
  templateUrl: './driver-history.component.html',
  styleUrls: ['./driver-history.component.scss', '../../../../../../../common-module/src/lib/app-styles/dialog-common.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CheckboxListFilterComponent,
    DateRangeFilterComponent,
    ButtonBackComponent,
    DriverHistoryTableComponent,
    TranslateModule,
    CommonModule,
    MaterialModule
  ]
})

export class DriverHistoryComponent implements OnInit {

  private readonly authUserService = inject(AuthUserService);
  private readonly driverHistoryService = inject(DriverHistoryService);
  private readonly driverManagementService = inject(DriverManagementService);
  private readonly viewBreakpointService = inject(ViewBreakpointService);
  private readonly uiSpinnerService = inject(UiSpinnerService);
  private readonly unitGroupsService = inject(UnitGroupsService);

  public readonly history = signal<DriverHistory[]>([]);
  public readonly pagination = new ServerTablePagination({
    initialPageSize: PageParams.DEFAULT_LIST[1],
    pageSizeOptions: PageParams.DEFAULT_LIST,
  });
  public currentDriver: Driver;
  public filterMap: Map<string, string> = new Map();
  public filters: FiltersSettings = {
    'unitIdFilter': {
      operator: FilterOperation.IN,
      value: ''
    },
    'timeFilter': {
      operator: FilterOperation.BETWEEN,
      value: ''
    }
  };
  public size$: Observable<ViewSize>;
  public timeZone: string;
  public unitList: UnitShort[] = [];

  private selectedIdsList: number[] = [];
  private sort = PageParams.SORT_TIME;

  ngOnInit(): void {
    this.size$ = this.viewBreakpointService.size$;
    this.currentDriver = this.driverManagementService.historySelectedDriver;
    this.timeZone = this.authUserService.getInstantTimeZone();
    this.onGetHistory();
    this.unitList = this.unitGroupsService.getInstantWithoutGroups();
  }

  public dateRangeFilterChange(timeRange: InstantRange): void {
    this.filters['timeFilter'].value = timeRange;
    this.onGetHistory(true);
  }

  public unitListFilterChange(selectedUnitIdsList: number[]): void {
    this.selectedIdsList = selectedUnitIdsList;
    this.onGetHistory(true);
  }

  public onGetHistory(isFilterChange = false): void {
    if (isFilterChange) {
      this.pagination.resetPage();
    }
    this.uiSpinnerService.show();
    this.driverHistoryService.getByDriver(
      this.currentDriver.id,
      this.createPageParams(),
      this.composeFilterMap(this.filters),
    )
      .pipe(
        map(page => {
          this.pagination.applyResponse(page);
          return page.content;
        })
      ).subscribe(
      content => {
        this.history.set(content);
        this.uiSpinnerService.stop();
      }
    );
  }

  private composeFilterMap(object: FiltersSettings): Map<string, string> {
    if (this.selectedIdsList.length > 0) {
      this.filters['unitIdFilter'].value = this.selectedIdsList.join(',');
    } else {
      this.filters['unitIdFilter'].value = null;
    }
    const filterMap = new Map();
    for (const key in object) {
      if ((object[key].value as InstantRange)?.start) {
        filterMap.set(key, object[key].operator + InstantUtils.toIsoMilliseconds((object[key].value as InstantRange).start) + ','
          + InstantUtils.toIsoMilliseconds((object[key].value as InstantRange).end));
        continue;
      }
      if (object[key].value) {
        filterMap.set(key, object[key].operator + object[key].value);
      }
    }
    return filterMap;
  }

  public onSortData(sort: Sort): void {
    this.sort = PageParams.sortParamsToAscDesc(sort);
    this.pagination.resetPage();
    this.onGetHistory();
  }

  public onChangePage($event: PageEvent): void {
    this.pagination.applyPageEvent($event);
    this.onGetHistory();
  }

  public onReturn(): void {
    this.driverManagementService.changeState(DriverState.LIST);
  }

  private createPageParams(): PageParams {
    const pagination = this.pagination.requestState();
    const pageParams = new PageParams(this.sort, pagination.size, PageParams.DEFAULT_LIST);

    pageParams.page = pagination.page;

    return pageParams;
  }

}
